// Design frames: wireframes/24-billing-m3.html (24.2 plans, 24.11 active, 24.14 accounts).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Dialog,
  List,
  ListItem,
  Notice,
  Segmented,
  Skeleton,
  Tag,
  TextField,
  useSnackbar,
} from '@sauda/ui/kit';
import { Screen, useChrome } from '../app/chrome.js';
import { useAuth } from '../auth/AuthContext.js';
import { ApiError, api } from '../lib/api.js';
import { formatRupees, formatRupeesExact } from '../lib/money.js';
import { formatIstDate } from '../lib/time.js';
import type { BillingOverview } from '../lib/types.js';

const date = (iso: string | null) => (iso === null ? '' : formatIstDate(iso));

/** Plan, trial, invoices and payment (master 4.6, 7). Prices are shown with GST worked out. */
export function BillingScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const canManage = auth.allowed('billing.manage');
  const [params, setParams] = useSearchParams();
  const showPlans = params.get('plans') === '1';
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'month' | 'year' | null>(null);
  const [gstin, setGstin] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [confirmName, setConfirmName] = useState('');

  const overview = useQuery({
    queryKey: ['billing', auth.agencyId],
    queryFn: () => api<BillingOverview>('/billing'),
  });
  const refresh = async () => {
    await cache.invalidateQueries({ queryKey: ['billing'] });
    await cache.invalidateQueries({ queryKey: ['billing-status'] });
  };
  const checkout = useMutation({
    mutationFn: (planCode: string) =>
      api<{ url: string }>('/billing/checkout', { method: 'POST', body: { planCode } }),
    onSuccess: (result) => window.location.assign(result.url),
    onError: (e) =>
      setError(
        e instanceof ApiError && e.code === 'PLAN_TOO_SMALL'
          ? t('billing.planTooSmall')
          : t('billing.generic'),
      ),
  });
  const downgrade = useMutation({
    mutationFn: (planCode: string) =>
      api('/billing/downgrade', { method: 'POST', body: { planCode } }),
    onSuccess: async () => {
      setError(null);
      await refresh();
      snack({ text: t('billing.downgradeScheduled') });
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.code === 'PLAN_TOO_SMALL'
          ? t('billing.planTooSmall')
          : t('billing.generic'),
      ),
  });
  const keepPlan = useMutation({
    mutationFn: () => api('/billing/downgrade', { method: 'DELETE' }),
    onSuccess: refresh,
    onError: () => setError(t('billing.generic')),
  });
  const saveGstin = useMutation({
    mutationFn: () =>
      api('/billing/details', { method: 'PATCH', body: { gstin: gstin === '' ? null : gstin } }),
    onSuccess: async () => {
      setError(null);
      setGstin('');
      await refresh();
      snack({ text: t('billing.gstinSaved') });
    },
    onError: () => setError(t('billing.badGstin')),
  });
  const requestDeletion = useMutation({
    mutationFn: () => api('/agency/deletion', { method: 'POST', body: { confirmName } }),
    onSuccess: async () => {
      setError(null);
      setDeleting(false);
      setConfirmName('');
      await refresh();
    },
    onError: () => setError(t('billing.deletionNameWrong')),
  });
  const cancelDeletion = useMutation({
    mutationFn: () => api('/agency/deletion/cancel', { method: 'POST', body: {} }),
    onSuccess: refresh,
    onError: () => setError(t('billing.generic')),
  });
  const openPdf = async (id: string) => {
    const result = await api<{ url: string | null }>(`/billing/invoices/${id}/pdf`);
    if (result.url !== null) window.open(result.url, '_blank', 'noopener');
  };

  const data = overview.data;
  const accounts = auth.current?.role === 'accounts';
  const title = accounts ? t('nav.invoices') : t('billing.title');
  if (data === undefined)
    return (
      <Screen title={title} back={accounts ? undefined : '/'} large={accounts}>
        {overview.isError ? (
          <Notice tone="error">{t('billing.generic')}</Notice>
        ) : (
          <Skeleton height={120} />
        )}
      </Screen>
    );
  const sub = data.subscription;
  const current = data.plans.find((p) => p.current);
  const scheduled = data.plans.find((p) => p.code === sub.scheduledPlanCode);
  const periods = [...new Set(data.plans.map((p) => p.period).filter((p) => p !== 'none'))] as (
    | 'month'
    | 'year'
  )[];
  const shownPeriod = period ?? periods[0] ?? 'month';
  const plans = data.plans.filter((p) => p.period === shownPeriod || p.period === 'none');
  const fits = (p: (typeof plans)[number]) =>
    (p.limits.maxBranches === null || data.counts.branches <= p.limits.maxBranches) &&
    (p.limits.maxMembers === null || data.counts.members <= p.limits.maxMembers);
  const recommended = [...plans].filter(fits).sort((x, y) => x.pricePaise - y.pricePaise)[0];
  const usage = `${t('billing.branchCount', { count: data.counts.branches })} · ${t('billing.memberCount', { count: data.counts.members })}`;

  if (showPlans && canManage) {
    return (
      <Screen
        title={t('billing.title')}
        back={() => setParams({}, { replace: true })}
        className="billing"
      >
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        <p className="k-t-body">{t('billing.usageSentence', { usage })}</p>
        {periods.length > 1 ? (
          <Segmented
            block
            label={t('billing.period')}
            value={shownPeriod}
            onChange={(v) => setPeriod(v as 'month' | 'year')}
            options={periods.map((p) => ({
              value: p,
              label: p === 'month' ? t('billing.monthly') : t('billing.yearly'),
            }))}
          />
        ) : null}
        {plans.map((p) => {
          const isCurrent = p.current && sub.status === 'active';
          const cheaper =
            current !== undefined && sub.status === 'active' && p.pricePaise < current.pricePaise;
          const fitting = fits(p);
          return (
            <Card key={p.code} className="billing__plan">
              <div className="card-head">
                <h3 className="k-t-title-sm">{p.name}</h3>
                {isCurrent ? (
                  <Tag>{t('billing.current')}</Tag>
                ) : recommended?.code === p.code ? (
                  <Tag tone="ok">{t('billing.recommended')}</Tag>
                ) : null}
              </div>
              <div>
                <p className="k-t-label-sm">
                  {p.period === 'month'
                    ? t('billing.perMonthExGst')
                    : p.period === 'year'
                      ? t('billing.perYearExGst')
                      : t('billing.exGst')}
                </p>
                <p className="k-t-display-sm">{formatRupees(p.quote.subtotalPaise)}</p>
                <p className="k-t-label-sm k-muted">
                  {t('billing.withGst', { amount: formatRupeesExact(p.quote.totalPaise) })}
                </p>
              </div>
              {p.quote.lines.map((l) => (
                <p key={l.description} className="k-t-label-sm">
                  {l.description}: {l.quantity} x {formatRupees(l.unitPaise)}
                </p>
              ))}
              {isCurrent ? null : !fitting ? (
                <Button disabled>
                  {t('billing.tooSmallFor', { branches: data.counts.branches })}
                </Button>
              ) : canManage ? (
                <Button
                  size="lg"
                  variant={cheaper ? 'tonal' : 'filled'}
                  loading={(cheaper ? downgrade : checkout).isPending}
                  onClick={() => (cheaper ? downgrade.mutate(p.code) : checkout.mutate(p.code))}
                >
                  {cheaper
                    ? t('billing.switchAtRenewal')
                    : t('billing.chooseName', { name: p.name })}
                </Button>
              ) : null}
            </Card>
          );
        })}
      </Screen>
    );
  }

  const statusTone =
    sub.status === 'active' ? 'ok' : sub.status === 'trialing' ? 'neutral' : 'warn';
  return (
    <Screen title={title} back={accounts ? undefined : '/'} large={accounts} className="billing">
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      <Card>
        <div className="card-head">
          <h2 className="k-t-title-sm">
            {t('billing.planName', { name: current?.name ?? sub.planCode })}
          </h2>
          <span data-testid="billing-status">
            <Tag tone={statusTone}>{t(`billing.status_${sub.status}`)}</Tag>
          </span>
        </div>
        {sub.status === 'trialing' ? (
          <p className="k-t-body">
            {t('billing.trialLeft', { count: sub.trialDaysLeft ?? 0, date: date(sub.trialEndsAt) })}
          </p>
        ) : null}
        {sub.status === 'past_due' ? (
          <p className="k-t-body">
            {t('billing.graceLeft', { date: date(sub.graceEndsAt ?? sub.currentPeriodEnd) })}
          </p>
        ) : null}
        {sub.status === 'active' && sub.currentPeriodEnd ? (
          <p className="k-t-body">
            {t('billing.renewsOn', { date: date(sub.currentPeriodEnd) })}
            {current === undefined ? '' : ` · ${formatRupeesExact(current.quote.totalPaise)}`}
          </p>
        ) : null}
        {accounts ? null : <p className="k-t-label-sm">{usage}</p>}
        {canManage ? (
          <Button variant="tonal" size="lg" onClick={() => setParams({ plans: '1' })}>
            {sub.status === 'trialing' ? t('billing.choosePlan') : t('billing.changePlan')}
          </Button>
        ) : null}
      </Card>
      {scheduled === undefined ? null : (
        <Notice
          tone="info"
          action={
            canManage ? (
              <Button variant="text" loading={keepPlan.isPending} onClick={() => keepPlan.mutate()}>
                {t('billing.keepPlan')}
              </Button>
            ) : undefined
          }
        >
          {t('billing.switchesTo', { plan: scheduled.name, date: date(sub.currentPeriodEnd) })}
        </Notice>
      )}
      <section aria-label={t('billing.invoices')} className="today-section">
        <h2 className="k-t-title-sm">{t('billing.invoices')}</h2>
        {data.invoices.length === 0 ? (
          <p className="k-t-body k-muted">{t('billing.noInvoices')}</p>
        ) : (
          <List label={t('billing.invoices')}>
            {[...data.invoices]
              .sort((x, y) => (y.paidAt ?? '').localeCompare(x.paidAt ?? ''))
              .map((i) => (
                <ListItem
                  key={i.id}
                  title={i.number}
                  supporting={[
                    date(i.paidAt),
                    accounts
                      ? formatRupeesExact(i.totalPaise)
                      : t('billing.inclGst', { amount: formatRupeesExact(i.totalPaise) }),
                    accounts ? null : formatRupeesExact(i.gstPaise),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  trailing={
                    i.hasPdf ? (
                      <Button variant="text" onClick={() => void openPdf(i.id)}>
                        {t('billing.pdf')}
                      </Button>
                    ) : (
                      <Tag>{t('billing.preparing')}</Tag>
                    )
                  }
                />
              ))}
          </List>
        )}
      </section>
      {canManage ? (
        <>
          <Card>
            <h2 className="k-t-title-sm">{t('billing.gstDetails')}</h2>
            <TextField
              label={t('billing.gstin')}
              placeholder={data.details.gstin ?? ''}
              supporting={t('billing.gstinHelp')}
              value={gstin}
              onChange={(e) => setGstin(e.target.value)}
            />
            <Button
              variant="tonal"
              size="lg"
              loading={saveGstin.isPending}
              disabled={chrome.readOnly}
              onClick={() => saveGstin.mutate()}
            >
              {t('billing.saveGstin')}
            </Button>
          </Card>
          <Card variant="outlined">
            <h2 className="k-t-title-sm">{t('billing.dangerZone')}</h2>
            <p className="k-t-body">{t('billing.deleteHelp')}</p>
            <div className="chips-row">
              <Button variant="text" destructive onClick={() => setDeleting(true)}>
                {t('billing.deleteAgency')}
              </Button>
              <Button
                variant="text"
                loading={cancelDeletion.isPending}
                onClick={() => cancelDeletion.mutate()}
              >
                {t('billing.cancelDeletion')}
              </Button>
            </div>
          </Card>
        </>
      ) : null}
      <Dialog
        open={deleting}
        onClose={() => setDeleting(false)}
        title={t('billing.deleteTitle')}
        destructive
        actions={
          <>
            <Button variant="text" onClick={() => setDeleting(false)}>
              {t('app.cancel')}
            </Button>
            <Button
              destructive
              loading={requestDeletion.isPending}
              disabled={confirmName.trim() === ''}
              onClick={() => requestDeletion.mutate()}
            >
              {t('billing.deleteAgency')}
            </Button>
          </>
        }
      >
        <div className="form-stack">
          <span>{t('billing.deleteHelp')}</span>
          <TextField
            label={t('billing.deleteConfirm', { name: data.details.name })}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
          />
        </div>
      </Dialog>
    </Screen>
  );
}
